import { createSlice, PayloadAction, isAnyOf } from "@reduxjs/toolkit";
import { sampleRequestModal } from "../../models/sampleRequestModel";
import {
  getSampleRequests,
  getSampleRequestStatusCounts,
  getSampleRequestById,
  createSampleRequest,
  updateSampleRequest,
  approveSampleRequest,
  rejectSampleRequest,
  requestSampleRequestChanges,
  submitSampleRequestChanges,
  submitSampleRequestToFactory,
  approveSampleRequestCAD,
  submitSampleRequestProduction,
  confirmSampleRequestReceived,
  cancelSampleRequest,
} from "../dispatcher/sampleRequestDispatcher";

interface SampleRequestState {
  list: Array<sampleRequestModal>;
  isListLoaded: boolean;
  loadingList: boolean;
  totalCount: number;
  totalPages: number;
  page: number;
  pageSize: number;

  // Filters — mirror the delivery slice's flat filter fields
  group: string;
  status: string | null;
  search: string;

  // Tab badge counts (SalesSampleRequestScreen's STATUS_TAB_GROUPS)
  statusCounts: Record<string, number>;
  loadingStatusCounts: boolean;

  // Currently open request on the details screen
  selected: sampleRequestModal | null;
  loadingSelected: boolean;

  // Shared across every mutation (create + all workflow transitions)
  actionLoading: boolean;
  actionError: string | null;
}

const initialState: SampleRequestState = {
  list: [],
  isListLoaded: false,
  loadingList: false,
  totalCount: 0,
  totalPages: 0,
  page: 1,
  pageSize: 20,

  group: "All",
  status: null,
  search: "",

  statusCounts: {},
  loadingStatusCounts: false,

  selected: null,
  loadingSelected: false,

  actionLoading: false,
  actionError: null,
};

// Every workflow-transition thunk shares the exact same
// pending/fulfilled/rejected shape — the backend always returns the full
// updated document, so fulfilled just needs to patch that row into `list`
// and `selected` in place. Grouped with isAnyOf instead of 27 near-
// identical addCase blocks (9 thunks × 3 cases).
const mutationThunks = [
  updateSampleRequest,
  approveSampleRequest,
  rejectSampleRequest,
  requestSampleRequestChanges,
  submitSampleRequestChanges,
  submitSampleRequestToFactory,
  approveSampleRequestCAD,
  submitSampleRequestProduction,
  confirmSampleRequestReceived,
  cancelSampleRequest,
];

const sampleRequestSlice = createSlice({
  name: "sampleRequest",
  initialState,
  reducers: {
    setSampleRequestGroup: (state, action: PayloadAction<string>) => {
      state.group = action.payload;
      state.status = null;
      state.page = 1;
    },
    setSampleRequestStatus: (state, action: PayloadAction<string | null>) => {
      state.status = action.payload;
      state.page = 1;
    },
    searchSampleRequests: (state, action: PayloadAction<string>) => {
      state.search = action.payload;
      state.page = 1;
    },
    setSampleRequestPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setSampleRequestPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.page = 1;
    },
    clearSelectedSampleRequest: (state) => {
      state.selected = null;
    },
    clearSampleRequestActionError: (state) => {
      state.actionError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ---- list ----
      .addCase(getSampleRequests.pending, (state) => {
        state.loadingList = true;
      })
      .addCase(getSampleRequests.fulfilled, (state, action) => {
        state.loadingList = false;
        state.isListLoaded = true;
        state.list = action.payload.data;
        state.totalCount = action.payload.pagination.totalCount;
        state.totalPages = action.payload.pagination.totalPages;
      })
      .addCase(getSampleRequests.rejected, (state) => {
        state.loadingList = false;
        state.isListLoaded = true;
      })

      // ---- status counts (tab badges) ----
      .addCase(getSampleRequestStatusCounts.pending, (state) => {
        state.loadingStatusCounts = true;
      })
      .addCase(getSampleRequestStatusCounts.fulfilled, (state, action) => {
        state.loadingStatusCounts = false;
        state.statusCounts = action.payload;
      })
      .addCase(getSampleRequestStatusCounts.rejected, (state) => {
        state.loadingStatusCounts = false;
      })

      // ---- get by id (details screen) ----
      .addCase(getSampleRequestById.pending, (state) => {
        state.loadingSelected = true;
      })
      .addCase(getSampleRequestById.fulfilled, (state, action) => {
        state.loadingSelected = false;
        state.selected = action.payload;
      })
      .addCase(getSampleRequestById.rejected, (state) => {
        state.loadingSelected = false;
      })

      // ---- create ----
      .addCase(createSampleRequest.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(createSampleRequest.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.list = [action.payload, ...state.list];
        state.totalCount += 1;
      })
      .addCase(createSampleRequest.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError =
          (action.payload as any)?.message || "Failed to create sample request";
      })

      // ---- every workflow transition + generic edit ----
      .addMatcher(isAnyOf(...mutationThunks.map((t) => t.pending)), (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addMatcher(
        isAnyOf(...mutationThunks.map((t) => t.fulfilled)),
        (state, action) => {
          state.actionLoading = false;
          const updated = action.payload;
          state.list = state.list.map((item: any) =>
            item._id === updated._id ? updated : item,
          );
          if (state.selected && (state.selected as any)._id === updated._id) {
            state.selected = updated;
          }
        },
      )
      .addMatcher(
        isAnyOf(...mutationThunks.map((t) => t.rejected)),
        (state, action) => {
          state.actionLoading = false;
          state.actionError =
            (action.payload as any)?.message || "Action failed";
        },
      );
  },
});

export const {
  setSampleRequestGroup,
  setSampleRequestStatus,
  searchSampleRequests,
  setSampleRequestPage,
  setSampleRequestPageSize,
  clearSelectedSampleRequest,
  clearSampleRequestActionError,
} = sampleRequestSlice.actions;

export const sampleRequestReducer = sampleRequestSlice.reducer;
